import React, { useCallback } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { type NativeScrollEvent, type NativeSyntheticEvent, StyleSheet, View } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { FlashList, type ListRenderItemInfo } from '@shopify/flash-list';

import { theme } from '@sg/garnish';

import { BUNDLE_TRANSITION_DURATION } from '../../Bundle.constants';
import { type BundleSelectProduct } from '../../Bundle.types';
import { BundleProductCard } from '../BundleProductCard';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleSelectList = (props: Props) => {
  const { products, selectedProductSlug, onSelect } = props;

  // ─────────────────────────────────────────────────────────────────────

  const topGradientOpacitySV = useSharedValue(0);
  const bottomGradientOpacitySV = useSharedValue(products.length > VISIBLE_CARDS ? 1 : 0);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;

      const remainingOffset = contentSize.height - layoutMeasurement.height - contentOffset.y;

      topGradientOpacitySV.value = withTiming(contentOffset.y > SCROLL_THRESHOLD ? 1 : 0, GRADIENT_TIMING); // prettier-ignore
      bottomGradientOpacitySV.value = withTiming(remainingOffset > SCROLL_THRESHOLD ? 1 : 0, GRADIENT_TIMING); // prettier-ignore
    },
    [bottomGradientOpacitySV, topGradientOpacitySV],
  );

  const renderItem = useCallback(
    (info: ListRenderItemInfo<BundleSelectProduct>) => {
      const { item, index } = info;

      return (
        <View style={index % COLUMNS === 0 ? styles.cell : styles.cellTrailing}>
          <BundleProductCard
            product={item}
            isSelected={item.slug === selectedProductSlug}
            onSelect={onSelect}
          />
        </View>
      );
    },
    [onSelect, selectedProductSlug],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const topGradientAnimatedStyles = useAnimatedStyle(() => ({
    opacity: topGradientOpacitySV.value,
  }));

  const bottomGradientAnimatedStyles = useAnimatedStyle(() => ({
    opacity: bottomGradientOpacitySV.value,
  }));

  const topGradientStyles = [styles.topGradient, topGradientAnimatedStyles];
  const bottomGradientStyles = [styles.bottomGradient, bottomGradientAnimatedStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <FlashList
        data={products}
        numColumns={COLUMNS}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        scrollEventThrottle={SCROLL_EVENT_THROTTLE}
        onScroll={handleScroll}
      />

      <Animated.View pointerEvents="none" style={topGradientStyles}>
        <LinearGradient
          colors={TOP_GRADIENT_COLORS}
          end={GRADIENT_END}
          locations={GRADIENT_LOCATIONS}
          start={GRADIENT_START}
          style={styles.gradientFill}
        />
      </Animated.View>

      <Animated.View pointerEvents="none" style={bottomGradientStyles}>
        <LinearGradient
          colors={BOTTOM_GRADIENT_COLORS}
          end={GRADIENT_END}
          locations={GRADIENT_LOCATIONS}
          start={GRADIENT_START}
          style={styles.gradientFill}
        />
      </Animated.View>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function keyExtractor(product: BundleSelectProduct) {
  return product.id;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const COLUMNS = 2;
const GRID_GAP = theme.spacing['2'];

const VISIBLE_ROWS = 2;
const VISIBLE_CARDS = COLUMNS * VISIBLE_ROWS;

const LIST_HEIGHT = 420;

const SCROLL_EVENT_THROTTLE = 32;
const SCROLL_THRESHOLD = 4;

const GRADIENT_HEIGHT = theme.spacing['12'];
const GRADIENT_START = { x: 0, y: 0 } as const;
const GRADIENT_END = { x: 0, y: 1 } as const;
const GRADIENT_TIMING = { duration: BUNDLE_TRANSITION_DURATION } as const;

const GRADIENT_LOCATIONS = [0, 0.2, 0.4, 0.6, 0.8, 1] as const;
const TOP_GRADIENT_COLORS = [
  'rgba(255, 255, 255, 1)',
  'rgba(255, 255, 255, 0.97)',
  'rgba(255, 255, 255, 0.86)',
  'rgba(255, 255, 255, 0.62)',
  'rgba(255, 255, 255, 0.28)',
  'rgba(255, 255, 255, 0)',
] as const;
const BOTTOM_GRADIENT_COLORS = [
  'rgba(255, 255, 255, 0)',
  'rgba(255, 255, 255, 0.28)',
  'rgba(255, 255, 255, 0.62)',
  'rgba(255, 255, 255, 0.86)',
  'rgba(255, 255, 255, 0.97)',
  'rgba(255, 255, 255, 1)',
] as const;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    height: LIST_HEIGHT,
  },
  gradientFill: {
    flex: 1,
  },
  topGradient: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    height: GRADIENT_HEIGHT,
  },
  bottomGradient: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    height: GRADIENT_HEIGHT,
  },
  cell: {
    flex: 1,
    paddingRight: GRID_GAP / 2,
    paddingBottom: GRID_GAP,
  },
  cellTrailing: {
    flex: 1,
    paddingBottom: GRID_GAP,
    paddingLeft: GRID_GAP / 2,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  products: BundleSelectProduct[];
  selectedProductSlug?: string;
  onSelect: (product: BundleSelectProduct) => void;
};
